/**
 * HandleStore — remembers what a source points at, between sessions.
 *
 * FileSystemHandles are structured-cloneable, so IndexedDB can hold them. That
 * is what makes "resume where you left off" possible: on reopening a project the
 * handles come back and only need their permission re-confirmed.
 *
 * Two shapes, because a source is either a folder the photographer pointed at or
 * a set of files they picked one by one.
 */

const DB_NAME = 'm3cull-handles';
const STORE = 'handles';
const VERSION = 1;

export type StoredSource =
  | { sourceId: number; kind: 'directory'; handle: FileSystemDirectoryHandle }
  | { sourceId: number; kind: 'files'; name: string; files: FileSystemFileHandle[] };

/** Records written before loose-file support had no `kind`; they were all folders. */
type LegacyRecord = { sourceId: number; handle: FileSystemDirectoryHandle; kind?: undefined };

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'sourceId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = fn(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
      }),
  );
}

export class HandleStore {
  async saveDirectory(sourceId: number, handle: FileSystemDirectoryHandle): Promise<void> {
    await tx('readwrite', (store) => store.put({ sourceId, kind: 'directory', handle }));
  }

  async saveFiles(sourceId: number, name: string, files: FileSystemFileHandle[]): Promise<void> {
    await tx('readwrite', (store) => store.put({ sourceId, kind: 'files', name, files }));
  }

  async load(sourceId: number): Promise<StoredSource | null> {
    const record = await tx<StoredSource | LegacyRecord | undefined>('readonly', (store) =>
      store.get(sourceId),
    );
    if (!record) return null;

    if (record.kind === undefined) {
      return { sourceId: record.sourceId, kind: 'directory', handle: record.handle };
    }
    return record;
  }

  async remove(sourceId: number): Promise<void> {
    await tx('readwrite', (store) => store.delete(sourceId));
  }

  async clear(): Promise<void> {
    await tx('readwrite', (store) => store.clear());
  }
}
